import React, { useEffect, useState } from "react";
import { isAxiosError } from "axios";
import { Link } from "react-router-dom";
import { Container, ProgressBar, Form, Alert, Spinner, Button } from "react-bootstrap";
import { FaTimes, FaArrowUp, FaArrowDown, FaShieldAlt } from "react-icons/fa";
import { getSiteStoragePaginated, SiteStorageSummaryDto } from "@/api/sharepointApi";
import AdminGroupDetail from "./AdminGroupDetail";
import "@/pages/Admin/AdminGroups.scss";

interface TabInfo {
  id: string;           // GUID
  title: string;        // site title
}

function formatGb(mb: number): string {
  return (mb / 1024).toFixed(1) + " GB";
}

function progressVariant(pct: number): string {
  if (pct > 85) return "danger";
  if (pct > 70) return "warning";
  return "success";
}

function formatDate(iso: string | null): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString();
}

type SortColumn = "site" | "storage" | "usage" | "snapshot" | "template";
type SiteType = "All Sites" | "SharePoint" | "OneDrive";

const AdminGroupsTabbed: React.FC = () => {
  const [sites, setSites] = useState<SiteStorageSummaryDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [query, setQuery] = useState({
    search: "", siteType: "All Sites" as SiteType, sortColumn: "storage" as SortColumn,
    sortDirection: "desc" as "asc" | "desc", page: 1, pageSize: 50,
  });
  const { search, siteType: siteTypeFilter, sortColumn, sortDirection, page, pageSize } = query;
  const [totalCount, setTotalCount] = useState(0);
  const [reload, setReload] = useState(0);
  const isFiltering = searchTerm.trim() !== search;
  const pageCount = Math.max(1, Math.ceil(totalCount / pageSize));

  // Tabs state
  const [openTabs, setOpenTabs] = useState<TabInfo[]>([
    { id: "all-groups", title: "All Groups" }
  ]);
  const [activeTabId, setActiveTabId] = useState("all-groups");

  useEffect(() => {
    const abort = new AbortController();
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await getSiteStoragePaginated((page - 1) * pageSize, pageSize, sortColumn, {
          sortDirection, search, siteType: siteTypeFilter, signal: abort.signal,
        });
        if (abort.signal.aborted) return;
        const lastPage = Math.max(1, Math.ceil(data.totalCount / pageSize));
        if (page > lastPage) {
          setQuery((current) => ({ ...current, page: lastPage }));
          return;
        }
        setSites(data.items);
        setTotalCount(data.totalCount);
      } catch (err: unknown) {
        if (abort.signal.aborted) return;
        setError(isAxiosError<{ error?: string }>(err)
          ? err.response?.data?.error ?? err.message
          : "Failed to load site storage.");
      } finally {
        if (!abort.signal.aborted) setLoading(false);
      }
    };
    load();
    return () => abort.abort();
  }, [page, pageSize, sortColumn, sortDirection, search, siteTypeFilter, reload]);

  // Debounced search effect
  useEffect(() => {
    const timeout = setTimeout(() => {
      const nextSearch = searchTerm.trim();
      setQuery((current) => current.search === nextSearch ? current : { ...current, search: nextSearch, page: 1 });
    }, 300);

    return () => clearTimeout(timeout);
  }, [searchTerm]);

  const openGroupTab = (site: SiteStorageSummaryDto) => {
    setOpenTabs((prev) => {
      if (prev.some((t) => t.id === site.guid)) return prev;
      return [...prev, { id: site.guid, title: site.title || "(no name)" }];
    });
    setActiveTabId(site.guid);
  };

  const closeTab = (tabId: string, e: React.MouseEvent) => {
    e.stopPropagation();

    if (tabId === "all-groups") return;

    setOpenTabs((prev) => prev.filter((t) => t.id !== tabId));

    if (activeTabId === tabId) {
      setActiveTabId("all-groups");
    }
  };

  const handleHeaderClick = (column: SortColumn) => {
    setQuery((current) => ({
      ...current, sortColumn: column, page: 1,
      sortDirection: current.sortColumn === column && current.sortDirection === "asc" ? "desc" : "asc",
    }));
  };

  const renderSortIcon = (column: "site" | "storage" | "usage" | "snapshot" | "template") => {
    if (sortColumn !== column) return null;
    return sortDirection === "asc" ? (
      <FaArrowUp className="ms-1" style={{ fontSize: 12 }} />
    ) : (
      <FaArrowDown className="ms-1" style={{ fontSize: 12 }} />
    );
  };

  return (
    <Container fluid className="mt-4 admin-groups-tabbed">
      <div className="mb-4 d-flex justify-content-between align-items-start flex-wrap gap-2">
        <div>
          <h2 className="mb-1">Groups Admin</h2>
          <p className="text-muted mb-0">SharePoint site storage overview.</p>
        </div>
        <Link to="/admin/groups/check-site-permissions" className="btn btn-outline-primary btn-sm">
          <FaShieldAlt className="me-2" />
          Check Site Permissions
        </Link>
      </div>

      {/* Tabs */}
      <div className="admin-groups-tabs d-flex gap-2 mb-3 flex-wrap">
        {openTabs.map((tab) => (
          <div
            key={tab.id}
            className={`admin-group-tab${activeTabId === tab.id ? " active" : ""}`}
            onClick={() => setActiveTabId(tab.id)}
          >
            <span className="tab-title">{tab.title}</span>
            {tab.id !== "all-groups" && (
              <button
                className="tab-close-btn"
                onClick={(e) => closeTab(tab.id, e)}
                title="Close tab"
              >
                <FaTimes />
              </button>
            )}
          </div>
        ))}
      </div>

      {/* Content wrapper */}
      <div className="admin-groups-content-wrapper">
        {/* ALL GROUPS TAB */}
        {activeTabId === "all-groups" && (
          <div className="admin-groups-list-content">
            {error && (
              <Alert variant="danger">
                {error} <Button size="sm" variant="outline-danger" onClick={() => setReload((value) => value + 1)}>Retry</Button>
              </Alert>
            )}

            <div className="mb-3 d-flex gap-2 align-items-start" style={{ maxWidth: 560 }}>
              <div style={{ maxWidth: 320, width: "100%", position: "relative" }}>
                <Form.Control
                  type="search"
                  placeholder="Filter by name or URL…"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
                {isFiltering && (
                  <Spinner
                    animation="border"
                    size="sm"
                    style={{
                      position: "absolute",
                      right: 12,
                      top: "50%",
                      transform: "translateY(-50%)",
                      width: 18,
                      height: 18,
                    }}
                  />
                )}
              </div>
              <Form.Select
                value={siteTypeFilter}
                onChange={(e) => setQuery((current) => ({ ...current, siteType: e.target.value as SiteType, page: 1 }))}
                style={{ maxWidth: 180 }}
                aria-label="Filter by site type"
              >
                <option value="All Sites">All Sites</option>
                <option value="SharePoint">SharePoint</option>
                <option value="OneDrive">OneDrive</option>
              </Form.Select>
            </div>

            {loading ? (
              <div className="text-muted">Loading sites…</div>
            ) : !error && (
              <div style={{ position: "relative" }}>
                <div className="table-responsive">
                  <table className="table table-sm table-hover align-middle">
                    <thead className="table-light">
                      <tr>
                        <th
                          style={{ cursor: "pointer", userSelect: "none" }}
                          onClick={() => handleHeaderClick("site")}
                        >
                          <div className="d-flex align-items-center">
                            Site
                            {renderSortIcon("site")}
                          </div>
                        </th>
                        <th
                          style={{ cursor: "pointer", userSelect: "none" }}
                          onClick={() => handleHeaderClick("storage")}
                        >
                          <div className="d-flex align-items-center">
                            Used / Quota
                            {renderSortIcon("storage")}
                          </div>
                        </th>
                        <th
                          style={{ cursor: "pointer", userSelect: "none", minWidth: 140 }}
                          onClick={() => handleHeaderClick("usage")}
                        >
                          <div className="d-flex align-items-center">
                            Usage
                            {renderSortIcon("usage")}
                          </div>
                        </th>
                        <th
                          style={{ cursor: "pointer", userSelect: "none" }}
                          onClick={() => handleHeaderClick("snapshot")}
                        >
                          <div className="d-flex align-items-center">
                            Last Snapshot
                            {renderSortIcon("snapshot")}
                          </div>
                        </th>
                        <th
                          style={{ cursor: "pointer", userSelect: "none" }}
                          onClick={() => handleHeaderClick("template")}
                        >
                          <div className="d-flex align-items-center">
                            Template
                            {renderSortIcon("template")}
                          </div>
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {sites.length === 0 && (
                        <tr>
                          <td colSpan={5} className="text-muted text-center py-3">No sites found.</td>
                        </tr>
                      )}
                      {sites.map((site) => (
                        <tr
                          key={site.siteCollectionId}
                          onClick={() => openGroupTab(site)}
                          className="cursor-pointer"
                        >
                          <td>
                            <span className="fw-semibold">{site.title || "(no name)"}</span>
                            <div
                              className="text-muted small"
                              style={{ maxWidth: 300, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                              title={site.url}
                            >
                              {site.url}
                            </div>
                          </td>
                          <td className="text-nowrap">
                            {formatGb(site.storageUsedMb)} / {formatGb(site.storageQuotaMb)}
                          </td>
                          <td>
                            <ProgressBar
                              now={Math.min(site.storageUsagePercent, 100)}
                              variant={progressVariant(site.storageUsagePercent)}
                              label={`${site.storageUsagePercent.toFixed(1)}%`}
                              style={{ minWidth: 120 }}
                            />
                          </td>
                          <td className="text-nowrap">{formatDate(site.lastSnapshotDate)}</td>
                          <td>
                            <span className="badge border text-secondary">{site.template ?? "—"}</span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {!loading && !error && (
              <div className="d-flex flex-wrap gap-3 align-items-center justify-content-between mt-2">
                <span className="text-muted small" role="status">
                  Showing {totalCount === 0 ? 0 : (page - 1) * pageSize + 1}–{Math.min(page * pageSize, totalCount)} of {totalCount} matching sites
                </span>
                <div className="d-flex gap-2 align-items-center">
                  <Form.Select
                    size="sm" aria-label="Sites per page" style={{ width: "auto" }} value={pageSize}
                    onChange={(e) => setQuery((current) => ({ ...current, pageSize: Number(e.target.value), page: 1 }))}
                  >
                    {[25, 50, 100].map((size) => <option key={size} value={size}>{size} per page</option>)}
                  </Form.Select>
                  <Button size="sm" variant="outline-secondary" disabled={page <= 1 || isFiltering}
                    onClick={() => setQuery((current) => ({ ...current, page: current.page - 1 }))}>Previous</Button>
                  <span className="small">Page {page} of {pageCount}</span>
                  <Button size="sm" variant="outline-secondary" disabled={page >= pageCount || isFiltering}
                    onClick={() => setQuery((current) => ({ ...current, page: current.page + 1 }))}>Next</Button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* DETAIL TABS */}
        {openTabs
          .filter((t) => t.id !== "all-groups")
          .map((tab) => (
            <div
              key={tab.id}
              className={`admin-group-detail-content${activeTabId === tab.id ? " active" : " d-none"}`}
            >
              <AdminGroupDetail siteGuid={tab.id} embedded />
            </div>
          ))}
      </div>
    </Container>
  );
};

export default AdminGroupsTabbed;
